import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtNumber } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import useBranchTypes from "@/hooks/useBranchTypes";
import { ChartBar, CaretUp, CaretDown, ArrowClockwise } from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { CHART_TOOLTIP_STYLE } from "@/lib/constants";
import RegistrationCostsReport from "@/components/RegistrationCostsReport";
import CashPositionReport from "@/components/CashPositionReport";
import FinancierReport from "@/components/FinancierReport";
import EvaluatorReport from "@/components/EvaluatorReport";
import { RetailDuesDetailReport, WriteOffExcessCombinedReport } from "@/components/DuesWriteOffExcessReports";
import { ReceivablesTab } from "@/pages/AccountsPage";
import { MODEL_CATEGORY_OPTIONS } from "@/components/PackageFormDrawer";
import {
  InventoryAgingReport, CashBookReport, BookingFunnelReport,
  SlaBreachesReport, SalespersonScoreboard, DiscountAuditReport,
} from "@/components/AdvancedReports";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function startOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function startOfQuarterISO() {
  const d = new Date();
  const qStart = Math.floor(d.getMonth() / 3) * 3;
  d.setMonth(qStart);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function startOfYearISO() {
  const d = new Date();
  d.setMonth(0);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

const PRESETS = [
  { id: "30d", label: "Last 30 days", from: () => daysAgoISO(30), to: () => todayISO() },
  { id: "mtd", label: "This month", from: () => startOfMonthISO(), to: () => todayISO() },
  { id: "qtd", label: "This quarter", from: () => startOfQuarterISO(), to: () => todayISO() },
  { id: "ytd", label: "This year", from: () => startOfYearISO(), to: () => todayISO() },
  { id: "all", label: "All time", from: () => "", to: () => "" },
];

function PresetTabs({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid="report-presets">
      {PRESETS.map((p) => {
        const active = value === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            data-testid={`report-preset-${p.id}`}
            className={`px-3 py-1.5 text-xs border transition-colors ${
              active
                ? "bg-ink text-white border-ink"
                : "border-canvas-line text-ink hover:border-ink"
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

function DateInput({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
        {label}
      </label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
      />
    </div>
  );
}

function DeltaBadge({ pct, prevWindow, testId }) {
  // `pct == null` → previous period was zero (or absent). Show a neutral
  // "n/a" pill rather than an infinity sign.
  if (pct == null) {
    return (
      <span
        data-testid={testId}
        className="font-mono text-[10px] tracking-wider uppercase text-ink-subtle"
      >
        — vs prev
      </span>
    );
  }
  const up = pct >= 0;
  const color = up ? "text-[#00713a]" : "text-destructive";
  const arrow = up ? "\u25b2" : "\u25bc"; // ▲ / ▼
  const sign = up && pct > 0 ? "+" : "";
  const window = prevWindow ? ` vs ${prevWindow.from} \u2192 ${prevWindow.to}` : " vs prev";
  return (
    <span
      data-testid={testId}
      className={`font-mono text-[10px] tracking-wider ${color}`}
      title={window.trim()}
    >
      {arrow} {sign}{pct}%
      <span className="text-ink-subtle ml-1 normal-case">vs prev period</span>
    </span>
  );
}

function TotalsStrip({ totals }) {
  const prevWindow = totals.prev_window;
  const cards = [
    {
      label: "Branches",
      value: fmtNumber(totals.branches),
      testId: "kpi-total-branches",
      // Branch count rarely changes period-over-period; suppress the delta.
      delta: undefined,
    },
    {
      label: "Total revenue",
      value: fmtMoney(totals.revenue),
      testId: "kpi-total-revenue",
      delta: totals.revenue_delta_pct,
      deltaTestId: "kpi-total-revenue-delta",
    },
    {
      label: "Total units sold",
      value: fmtNumber(totals.units_sold),
      testId: "kpi-total-units",
      delta: totals.units_sold_delta_pct,
      deltaTestId: "kpi-total-units-delta",
    },
  ];
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="kpi-totals">
      {cards.map((c) => (
        <div
          key={c.label}
          data-testid={c.testId}
          className="bg-canvas-card border border-canvas-line p-4"
        >
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink-muted">
            {c.label}
          </div>
          <div className="font-display text-2xl font-bold text-ink mt-2 tabular-nums">
            {c.value}
          </div>
          {c.delta !== undefined && (
            <div className="mt-2">
              <DeltaBadge pct={c.delta} prevWindow={prevWindow} testId={c.deltaTestId} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RevenueChart({ rows }) {
  // Compact dataset for recharts; one bar per branch.
  const data = rows.map((r) => ({
    name: r.branch_name.length > 14 ? r.branch_name.slice(0, 13) + "…" : r.branch_name,
    revenue: r.revenue,
    units: r.units_sold,
  }));
  if (data.length === 0) {
    return (
      <div
        className="border border-canvas-line bg-canvas-card p-10 text-center font-mono text-xs text-ink-subtle"
        data-testid="kpi-chart-empty"
      >
        No branches match the current filters.
      </div>
    );
  }
  return (
    <div
      className="bg-canvas-card border border-canvas-line p-4"
      data-testid="kpi-revenue-chart"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink-muted">
            Branch comparison
          </div>
          <div className="text-sm font-semibold text-ink mt-1">Revenue vs units sold</div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" vertical={false} />
          <XAxis
            dataKey="name"
            stroke="#5C5C5C"
            tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
          />
          <YAxis
            yAxisId="left"
            stroke="#002FA7"
            tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
            tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#0A0A0A"
            tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
          />
          <Tooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            formatter={(value, name) =>
              name === "Revenue" ? [fmtMoney(value), name] : [value, name]
            }
          />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
          <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#002FA7" />
          <Bar yAxisId="right" dataKey="units" name="Units" fill="#0A0A0A" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const SORT_COLS = [
  { id: "branch_name", label: "Branch", numeric: false },
  { id: "branch_type_name", label: "Type", numeric: false },
  { id: "revenue", label: "Revenue", numeric: true },
  { id: "units_sold", label: "Units", numeric: true },
];

function SortableHeader({ col, sort, onSort }) {
  const active = sort.id === col.id;
  const dir = active ? sort.dir : null;
  return (
    <th
      onClick={() => onSort(col.id)}
      data-testid={`kpi-sort-${col.id}`}
      className={`px-4 py-3 text-left text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted cursor-pointer select-none hover:text-ink ${
        col.numeric ? "text-right" : ""
      }`}
    >
      <span className="inline-flex items-center gap-1">
        {col.label}
        {active &&
          (dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </span>
    </th>
  );
}

function TopModelsCell({ models }) {
  if (!models.length) return <span className="text-ink-subtle">—</span>;
  return (
    <ul className="space-y-0.5">
      {models.map((m) => (
        <li key={m.name} className="flex items-center gap-2 text-xs">
          <span className="text-ink truncate">{m.name}</span>
          <span className="font-mono text-[10px] text-ink-muted shrink-0">×{m.units}</span>
        </li>
      ))}
    </ul>
  );
}

function KpiTable({ rows, sort, onSort }) {
  return (
    <div className="bg-canvas-card border border-canvas-line overflow-x-auto" data-testid="kpi-table">
      <table className="w-full text-sm">
        <thead className="border-b border-canvas-line bg-canvas-panel">
          <tr>
            {SORT_COLS.map((c) => (
              <SortableHeader key={c.id} col={c} sort={sort} onSort={onSort} />
            ))}
            <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted">
              Top models
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-4 py-10 text-center font-mono text-xs text-ink-subtle">
                No branches match the current filters.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={r.branch_id}
                data-testid={`kpi-row-${r.branch_id}`}
                className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel"
              >
                <td className="px-4 py-3">
                  <div className="font-semibold text-ink">{r.branch_name}</div>
                  <div className="font-mono text-[10px] text-ink-subtle uppercase tracking-wider">
                    {r.branch_code || "—"}
                  </div>
                </td>
                <td className="px-4 py-3 text-ink">{r.branch_type_name}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                  {fmtMoney(r.revenue)}
                  {r.revenue_delta_pct !== undefined && (
                    <div className="mt-0.5">
                      <DeltaBadge pct={r.revenue_delta_pct} testId={`kpi-row-revenue-delta-${r.branch_id}`} />
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                  {fmtNumber(r.units_sold)}
                  {r.units_sold_delta_pct !== undefined && (
                    <div className="mt-0.5">
                      <DeltaBadge pct={r.units_sold_delta_pct} testId={`kpi-row-units-delta-${r.branch_id}`} />
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 min-w-[200px]">
                  <TopModelsCell models={r.top_models} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function FilterBar({
  preset,
  setPreset,
  customFrom,
  setCustomFrom,
  customTo,
  setCustomTo,
  branchTypeId,
  setBranchTypeId,
  branchId,
  setBranchId,
  modelCategory,
  setModelCategory,
  types,
  branches,
  onRefresh,
}) {
  return (
    <div
      className="bg-canvas-card border border-canvas-line p-4 flex flex-wrap items-end gap-4"
      data-testid="kpi-filter-bar"
    >
      <div className="flex-1 min-w-[260px]">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
          Time window
        </div>
        <PresetTabs value={preset} onChange={setPreset} />
      </div>
      <DateInput
        label="From"
        value={customFrom}
        onChange={(v) => {
          setCustomFrom(v);
          setPreset("custom");
        }}
        testId="kpi-date-from"
      />
      <DateInput
        label="To"
        value={customTo}
        onChange={(v) => {
          setCustomTo(v);
          setPreset("custom");
        }}
        testId="kpi-date-to"
      />
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
          Branch Type
        </label>
        <select
          value={branchTypeId}
          onChange={(e) => setBranchTypeId(e.target.value)}
          data-testid="kpi-branch-type-filter"
          className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        >
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
          Branch
        </label>
        <select
          value={branchId}
          onChange={(e) => setBranchId(e.target.value)}
          data-testid="kpi-branch-filter"
          className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink max-w-[220px]"
        >
          <option value="">All branches</option>
          {branches
            .filter((b) => !branchTypeId || b.branch_type_id === branchTypeId)
            .map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
        </select>
      </div>
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
          Model
        </label>
        <select
          value={modelCategory}
          onChange={(e) => setModelCategory(e.target.value)}
          data-testid="kpi-model-filter"
          className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        >
          <option value="">All models</option>
          {MODEL_CATEGORY_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        data-testid="kpi-refresh"
        className="px-3 py-1.5 border border-canvas-line text-xs hover:border-ink flex items-center gap-1.5"
      >
        <ArrowClockwise size={12} weight="bold" />
        Refresh
      </button>
    </div>
  );
}

export default function ReportsPage() {
  const { types } = useBranchTypes();
  const [branches, setBranches] = useState([]);
  const [preset, setPreset] = useState("30d");
  const [customFrom, setCustomFrom] = useState(daysAgoISO(30));
  const [customTo, setCustomTo] = useState(todayISO());
  const [branchTypeId, setBranchTypeId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [modelCategory, setModelCategory] = useState("");
  const [data, setData] = useState({ rows: [], totals: { revenue: 0, units_sold: 0, branches: 0 } });
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ id: "revenue", dir: "desc" });
  const [tab, setTab] = useState("kpis");

  // Load the branch list once for the branch filter dropdown.
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  // Clear branchId if it no longer belongs to the selected branch type.
  useEffect(() => {
    if (!branchId) return;
    const b = branches.find((x) => x.id === branchId);
    if (b && branchTypeId && b.branch_type_id !== branchTypeId) {
      setBranchId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchTypeId]);

  // When a preset is picked, sync the date inputs (visual feedback).
  useEffect(() => {
    if (preset === "custom") return;
    const p = PRESETS.find((x) => x.id === preset);
    if (!p) return;
    setCustomFrom(p.from());
    setCustomTo(p.to());
  }, [preset]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (customFrom) params.date_from = customFrom;
      if (customTo) params.date_to = customTo;
      if (branchTypeId) params.branch_type_id = branchTypeId;
      if (branchId) params.branch_id = branchId;
      if (modelCategory) params.model_category = modelCategory;
      // Ask the API for previous-period totals + per-row deltas so the
      // KPI tiles can render "+18% vs prev 30d" badges. Backend gracefully
      // omits the prev fields when date_from / date_to are absent.
      if (customFrom && customTo) params.with_pop = true;
      const { data: res } = await api.get("/reports/branch-kpis", { params });
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Refetch whenever any filter changes.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFrom, customTo, branchTypeId, branchId, modelCategory]);

  const sortedRows = useMemo(() => {
    const copy = [...data.rows];
    copy.sort((a, b) => {
      const av = a[sort.id];
      const bv = b[sort.id];
      if (typeof av === "string") {
        return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sort.dir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [data.rows, sort]);

  const onSort = (colId) => {
    setSort((prev) =>
      prev.id === colId
        ? { id: colId, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { id: colId, dir: colId === "branch_name" || colId === "branch_type_name" ? "asc" : "desc" }
    );
  };

  return (
    <div data-testid="reports-page">
      <PageHeader
        eyebrow="04 / Reports"
        title="Branch KPIs"
        subtitle="Compare revenue, volume, and bestsellers across every branch in one view."
      />
      <div className="px-8 py-6 space-y-4">
        {/* Deep-linked report pages */}
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/reports/inventory-count-pivot"
            data-testid="link-inventory-pivot"
            className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-canvas-line hover:border-ink hover:bg-canvas-panel"
          >
            Inventory Count Pivot →
          </Link>
          <Link
            to="/reports/profit-per-vehicle"
            className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-canvas-line hover:border-ink hover:bg-canvas-panel"
          >
            Profit / Vehicle →
          </Link>
        </div>
        {/* Tab switcher */}
        <div className="inline-flex border border-canvas-line flex-wrap">
          {[
            ["kpis", "Branch KPIs"],
            ["regcosts", "Registration Costs"],
            ["inventory", "Inventory Aging"],
            ["cashbook", "Cash Book"],
            ["cashposition", "Cash Position"],
            ["funnel", "Booking Funnel"],
            ["sla", "SLA Breaches"],
            ["scoreboard", "Salesperson"],
            ["discount", "Discount Audit"],
            ["financier", "Financier"],
            ["evaluator", "Evaluator"],
            ["exchange", "Exchange Receivables"],
            ["dues", "Retail Dues"],
            ["writeoff-excess", "Write-Off & Excess"],
          ].map(([id, label], i) => (
            <button key={id} type="button" onClick={() => setTab(id)}
              data-testid={`report-tab-${id}`}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${i > 0 ? "border-l border-canvas-line" : ""} ${tab === id ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}>
              {label}
            </button>
          ))}
        </div>

        <FilterBar
          preset={preset}
          setPreset={setPreset}
          customFrom={customFrom}
          setCustomFrom={setCustomFrom}
          customTo={customTo}
          setCustomTo={setCustomTo}
          branchTypeId={branchTypeId}
          setBranchTypeId={setBranchTypeId}
          branchId={branchId}
          setBranchId={setBranchId}
          modelCategory={modelCategory}
          setModelCategory={setModelCategory}
          types={types}
          branches={branches}
          onRefresh={fetchData}
        />

        {tab === "kpis" ? (
          <>
            <TotalsStrip totals={data.totals} />
            <RevenueChart rows={sortedRows} />
            {loading ? (
              <div
                className="bg-canvas-card border border-canvas-line p-10 text-center font-mono text-xs text-ink-subtle"
                data-testid="kpi-loading"
              >
                <ChartBar size={20} weight="bold" className="inline-block animate-pulse" />
                <div className="mt-2">Crunching numbers…</div>
              </div>
            ) : (
              <KpiTable rows={sortedRows} sort={sort} onSort={onSort} />
            )}
          </>
        ) : tab === "regcosts" ? (
          <RegistrationCostsReport dateFrom={customFrom} dateTo={customTo} branchId={branchId} branchTypeId={branchTypeId} modelCategory={modelCategory} />
        ) : tab === "inventory" ? (
          <InventoryAgingReport branchId={branchId} branchTypeId={branchTypeId} modelCategory={modelCategory} />
        ) : tab === "cashbook" ? (
          <CashBookReport dateFrom={customFrom} dateTo={customTo} branchId={branchId} branchTypeId={branchTypeId} />
        ) : tab === "cashposition" ? (
          <CashPositionReport dateFrom={customFrom} dateTo={customTo} branchId={branchId} branchTypeId={branchTypeId} />
        ) : tab === "funnel" ? (
          <BookingFunnelReport dateFrom={customFrom} dateTo={customTo} branchId={branchId} branchTypeId={branchTypeId} modelCategory={modelCategory} />
        ) : tab === "sla" ? (
          <SlaBreachesReport branchId={branchId} branchTypeId={branchTypeId} modelCategory={modelCategory} />
        ) : tab === "scoreboard" ? (
          <SalespersonScoreboard dateFrom={customFrom} dateTo={customTo} branchId={branchId} branchTypeId={branchTypeId} modelCategory={modelCategory} />
        ) : tab === "discount" ? (
          <DiscountAuditReport dateFrom={customFrom} dateTo={customTo} branchId={branchId} branchTypeId={branchTypeId} modelCategory={modelCategory} />
        ) : tab === "financier" ? (
          <FinancierReport dateFrom={customFrom} dateTo={customTo} branchId={branchId} branchTypeId={branchTypeId} modelCategory={modelCategory} />
        ) : tab === "evaluator" ? (
          <EvaluatorReport dateFrom={customFrom} dateTo={customTo} branchId={branchId} branchTypeId={branchTypeId} modelCategory={modelCategory} />
        ) : tab === "exchange" ? (
          <ReceivablesTab kind="exchange" branchId={branchId} />
        ) : tab === "dues" ? (
          <RetailDuesDetailReport branchId={branchId} />
        ) : tab === "writeoff-excess" ? (
          <WriteOffExcessCombinedReport branchId={branchId} dateFrom={customFrom} dateTo={customTo} />
        ) : null}
      </div>
    </div>
  );
}
